import React, { useState } from 'react';
import { Box, Button, ToggleButton, ToggleButtonGroup, Popover, Typography, Stack, Alert } from '@mui/material';
import RangeCalendar from './RangeCalendar';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { PRESETS, presetPeriod, validateRange, earliestAllowed, AUTO_HISTORY_FROM } from '../../utils/period';
import { formatDate, formatDateShort, mskToday } from '../../utils/format';
import { tokens } from '../../theme';

export const toIso = (d) => {
  if (!d || Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
export const fromIso = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// Presets 7/30/60/90 days plus a custom range of any length (Moscow dates) from the earliest day
// WB serves. Days without loaded data are explained by the coverage notes of each page.
const PeriodPicker = ({ value, onChange }) => {
  const [anchor, setAnchor] = useState(null);
  const [from, setFrom] = useState(fromIso(value.from));
  const [to, setTo] = useState(fromIso(value.to));
  const today = mskToday();
  const error = anchor ? validateRange(toIso(from), toIso(to), today) : null;

  const open = (e) => {
    setFrom(fromIso(value.from));
    setTo(fromIso(value.to));
    setAnchor(e.currentTarget);
  };
  const apply = () => {
    if (error) return;
    onChange({ from: toIso(from), to: toIso(to), preset: null });
    setAnchor(null);
  };

  return (
    <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
      <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
        <ToggleButtonGroup
          exclusive
          value={value.preset || null}
          onChange={(_, v) => v && onChange(presetPeriod(v))}
          aria-label="Период"
          sx={{ maxWidth: '100%', overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }, bgcolor: tokens.paper, borderRadius: 3, '& .MuiToggleButton-root': { borderRadius: '10px !important', border: 'none', mx: { xs: 0, sm: 0.25 }, whiteSpace: 'nowrap', px: { xs: 1, sm: 2 }, fontSize: { xs: '0.8125rem', sm: '0.9375rem' } }, p: 0.5, border: `1px solid ${tokens.border}` }}
        >
          {PRESETS.map((d) => (
            <ToggleButton key={d} value={d} aria-label={`${d} дней`}>
              {d} дней
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Button
          variant="outlined"
          onClick={open}
          startIcon={<CalendarMonthOutlinedIcon />}
          aria-haspopup="dialog"
          sx={{ height: 48, borderRadius: 3, px: 2, ...(value.preset ? {} : { borderColor: tokens.primary, color: tokens.primaryDark, bgcolor: tokens.primaryTint }) }}
        >
          {value.preset ? 'Выбрать период' : `${formatDateShort(value.from)} – ${formatDateShort(value.to)}`}
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, textAlign: { md: 'right' } }}>
        {formatDate(value.from)} — {formatDate(value.to)} · время московское
      </Typography>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 2.5, width: 360, maxWidth: 'calc(100vw - 32px)' }, role: 'dialog', 'aria-label': 'Выбор периода' } }}
      >
        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>Произвольный период</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Любой период с {formatDate(earliestAllowed())} по {formatDate(today)}. Данные загружаются автоматически
          с {formatDate(AUTO_HISTORY_FROM)}; более раннюю историю можно запросить на странице «Состояние загрузки».
        </Typography>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {from ? formatDate(toIso(from)) : '…'} — {to ? formatDate(toIso(to)) : '…'}
          </Typography>
          <RangeCalendar from={toIso(from)} to={toIso(to)} min={earliestAllowed()} max={today}
            onChange={(r) => { setFrom(fromIso(r.from)); setTo(fromIso(r.to)); }} />
          <Typography variant="caption" color="text.secondary">
            Нажмите на первый день периода, затем на последний. Месяцы прокручиваются вверх и вниз.
          </Typography>
          {error && <Alert severity="warning" sx={{ py: 0 }}>{error}</Alert>}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => setAnchor(null)}>Отмена</Button>
            <Button variant="contained" onClick={apply} disabled={Boolean(error)}>Применить</Button>
          </Stack>
        </Stack>
      </Popover>
    </Box>
  );
};

export default PeriodPicker;
